import React, { FC, RefObject } from 'react'
import UnifiedPlayMark from './UnifiedPlayMark'

export interface VideoCanvasPresentationProps {
  videoUrl: string
  /** HLSマスタープレイリストURL。指定時はsrcをVideoCanvas側で動的設定する(MP4の二重ロードを避ける) */
  hlsUrl?: string | null
  isPlaying: boolean
  isMuted: boolean
  playbackRate?: number
  isLoading: boolean
  hasError: boolean
  showPlayButton: boolean
  isEnded: boolean
  onClick: () => void
  onTouchEnd: (e: React.TouchEvent) => void
  onReplay: () => void
  videoRef: RefObject<HTMLVideoElement | null>
  /**
   * 枠に対する動画の収め方。既定は `cover`（枠を埋めて余白を作らない＝縦型プレイヤー向け）。
   * ワイド型は 16:9 の枠に 16:9 の素材を入れるので `contain` にして、万一比率が
   * ずれていてもトリミングせずレターボックスで見せる（ADR-0016）。
   */
  fit?: 'cover' | 'contain'
  /** 終了時のリプレイ用オーバーレイを出さない。分岐ボタンや終了画面を上に出す場合に使う。 */
  suppressEndedOverlay?: boolean
}

const VideoCanvasPresentation: FC<VideoCanvasPresentationProps> = ({
  videoUrl,
  hlsUrl,
  isPlaying,
  isMuted,
  playbackRate = 1.0,
  isLoading,
  hasError,
  showPlayButton,
  isEnded,
  onClick,
  onTouchEnd,
  onReplay,
  videoRef,
  fit = 'cover',
  suppressEndedOverlay = false,
}) => {
  return (
    <div data-testid="video-container" className="absolute inset-0">
      {/* Video Element */}
      <video
        ref={videoRef}
        data-testid="video-element"
        // 視聴秒数を数える対象の目印（EmbeddingAiInterview の再生秒数カウンターが探す）
        data-moovy-main-video=""
        className="w-full h-full"
        // object-fit は Tailwind クラスではなくインラインで持つ。埋め込み先に配信する
        // tailwind.css は静的アセットでキャッシュされ得るため（BoxModal と同じ理由）。
        style={{ objectFit: fit }}
        // hlsUrl 指定時は VideoCanvas が src を動的設定する(MP4の二重ロード回避)
        src={hlsUrl ? undefined : videoUrl}
        muted={isMuted}
        // autoPlay 属性は使わない（再ロードのたびに再武装され、一時停止した動画が
        // 0:00 から再生されるため）。自動再生は VideoCanvas の effect が担当する。
        playsInline
        onClick={onClick}
        onTouchEnd={onTouchEnd}
        onLoadedMetadata={(e) => {
          const video = e.currentTarget
          video.playbackRate = playbackRate
        }}
      />

      {/* Loading Indicator */}
      {isLoading && (
        <div data-testid="video-loading" className="absolute inset-0 flex items-center justify-center bg-black/50">
          <div className="w-[48px] h-[48px] border-4 border-white/30 border-t-white rounded-full animate-spin" />
        </div>
      )}

      {/* Error Message */}
      {hasError && (
        <div data-testid="video-error" className="absolute inset-0 flex items-center justify-center bg-black/80">
          <div className="text-center text-white p-4">
            <svg
              className="w-[48px] h-[48px] mx-auto mb-2 text-red-500"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-[14px]">動画の読み込みに失敗しました</p>
          </div>
        </div>
      )}

      {/* Play/Pause Overlay Button */}
      {showPlayButton && !isPlaying && !isLoading && !hasError && !isEnded && (
        <div
          data-testid="play-pause-overlay"
          className="absolute inset-0 flex items-center justify-center pointer-events-none"
        >
          <button
            type="button"
            aria-label="再生"
            className="group flex h-[72px] w-[72px] items-center justify-center rounded-full pointer-events-auto"
            onClick={onClick}
          >
            <span data-testid="play-icon">
              <UnifiedPlayMark />
            </span>
          </button>
        </div>
      )}

      {/* Replay Button - Show when video ends */}
      {isEnded && !isLoading && !hasError && !suppressEndedOverlay && (
        <div data-testid="replay-overlay" className="absolute inset-0 flex items-center justify-center bg-black/20">
          <button
            className="group w-[80px] h-[80px] bg-black/50 rounded-full flex items-center justify-center hover:bg-black/70 transition-all duration-200 backdrop-blur-sm shadow-lg hover:shadow-xl border-2 border-white/30 hover:border-white/50"
            onClick={onReplay}
          >
            <svg
              data-testid="replay-icon"
              className="w-[40px] h-[40px] text-white transition-transform group-hover:rotate-180 duration-500"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              viewBox="0 0 24 24"
            >
              <path d="M1 4v6h6" />
              <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
            </svg>
          </button>
        </div>
      )}
    </div>
  )
}

export default VideoCanvasPresentation
